import { useQuery } from "@tanstack/react-query";
import { apiRequest } from "@core/services/api/apiRequest";
import {
  normalizeQuizResponseData,
  type Quiz,
  type QuizQuestionSet,
} from "@core/features/quiz";

type Args = {
  quizId: number;
  enabled?: boolean;
};

type QuizApiResponse = {
  id: number;
  name: string;
  type: string;
  title?: string;
  description?: string;
  questions?: Array<{ header: string; additional_info: string; [key: string]: unknown }>;
  question_sets?: Array<{
    header: string;
    description?: string;
    questions: Array<unknown>;
  }>;
  [key: string]: unknown;
};

/**
 * Normalize quiz API response to Quiz type.
 * Handles both formats:
 * - `questions` array (needs grouping into questionSets)
 * - `question_sets` array (already grouped, just needs camelCase conversion)
 */
function normalizeQuizData(data: QuizApiResponse): Quiz {
  // If data already has question_sets (snake_case from API), convert to camelCase
  if (data.question_sets && Array.isArray(data.question_sets)) {
    return {
      ...data,
      id: String(data.id),
      questionSets: data.question_sets as QuizQuestionSet[],
    } as unknown as Quiz;
  }

  // If data has questions array, use the standard normalization
  if (data.questions && Array.isArray(data.questions)) {
    return normalizeQuizResponseData(
      data as { questions: Array<{ header: string; additional_info: string; [key: string]: unknown }>; [key: string]: unknown }
    ) as unknown as Quiz;
  }

  // Fallback: return data as-is with empty questionSets
  return {
    ...data,
    id: String(data.id),
    questionSets: [],
  } as unknown as Quiz;
}

/**
 * Fetch quiz structure without authentication.
 * Returns the quiz with questions and choices for anonymous quiz flows.
 */
export function useGetAnonymousQuiz({ quizId, enabled = true }: Args) {
  return useQuery({
    queryKey: ["quiz", quizId],
    queryFn: async () => {
      const response = await apiRequest(
        `/quiz/${quizId}/`,
        "GET",
        null,
        false // requireAuth = false
      );
      // API returns {quiz: {...}}, extract the nested quiz object
      const data = response.quiz ?? response;
      return normalizeQuizData(data);
    },
    enabled: enabled && !!quizId,
  });
}
